import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAppSelector } from "@redux/hooks";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { carrierAccessColumns as baseColumns } from "./CarrierAccessData";
import { makeCarrierAccessFields } from "./carrierAccessFieldSpec";
import { useCarrierAccessList } from "./useCarrierAccessList";
import type { CarrierAccessItem } from "./carrierAccess.types";

/**
 * Carrier Access — list view (Kontrol Edge, CTX-652).
 *
 * Same shape as the Extended PoP list: the whole tenant list is fetched once
 * and filtered client-side by the advanced searchbar (by type, carrier,
 * customer, endpoint…). The Customer column appears only in the partner's
 * "all customers" view.
 */
const MainCarrierAccess: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const { items, loading, error } = useCarrierAccessList();

  const overlaySpinner = useProcessingOverlay(loading);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  const fields = useMemo(
    () => makeCarrierAccessFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const suggestedKeys = useMemo(() => fields.map((f) => f.label), [fields]);

  const predicate = useMemo(
    () =>
      buildPredicate<CarrierAccessItem>(
        normalizeFilters(activeFilters, fields),
        fields
      ),
    [activeFilters, fields]
  );

  const filteredData = useMemo(
    () => items.filter(predicate),
    [items, predicate]
  );

  const columns = useMemo<GenericTableColumn<CarrierAccessItem>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: CarrierAccessItem) => row.customer?.name ?? "",
        renderCell: (row: CarrierAccessItem) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }

    return cols.map((col) =>
      col.header === "Carrier Access ID"
        ? {
            ...col,
            // Same link treatment MainPort and Extended PoP give their ids.
            renderCell: (row: CarrierAccessItem) => (
              <button
                onClick={() =>
                  navigate(`/kontrol/carrier-access-details?id=${row.id}`)
                }
                className="
                  hover:text-semantic-text-brand
                  hover:underline
                  focus:underline
                  active:underline
                  font-medium
                  underline
                  text-semantic-text-brand
                "
              >
                {row.serviceId}
              </button>
            ),
          }
        : col
    );
  }, [isViewingAllCustomers, navigate]);

  if (loading) {
    return overlaySpinner;
  }
  if (error) {
    return <ErrorFetchingItems text={error} />;
  }
  if (items.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative p-4 overflow-hidden flex flex-col h-full">
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-3 my-5">
                  <div className="w-full max-w-full sm:max-w-[500px]">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <GenericTable
                  columns={columns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  maxTableBodyHeight="100%"
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MainCarrierAccess;
